import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";

const PARTNER_DESIGNATION_IDS = [117];

const isDisciplinaryDocument = (record = {}) => {
  const documentType = String(record.document_type || "");
  const subject = String(record.subject || "");
  if (documentType === "Warning Letter") return true;
  return /warning|misconduct|disciplin/i.test(`${documentType} ${subject}`);
};

const emptyReportState = {
  employees: [],
  attendanceRows: [],
  leaveRows: [],
  salaryRows: [],
  commissionRows: [],
  fineRows: [],
  disciplinaryRows: [],
  advanceRows: [],
  loanRows: [],
  promotionRows: [],
  profileEntryRows: [],
  summary: {
    employees: 0,
    present: 0,
    late: 0,
    absent: 0,
    onLeave: 0,
    netSalary: 0,
    grossCommission: 0,
    fines: 0,
    disciplinary: 0,
    advances: 0,
    loanOutstanding: 0,
  },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(toNumber(value));

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "N/A");

const formatDateTime = (value, inputFormat) => {
  if (!value) return "N/A";
  const parsed = inputFormat ? moment(value, inputFormat) : moment(value);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const buildEmployeeName = (employee = {}, fallback = "N/A") => {
  const name = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return name || fallback;
};

const normalizeRows = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const rowEmployeeId = (row = {}) => {
  const id =
    row.user_id ??
    row.attendee_id ??
    row.employee_id ??
    row.staff_id ??
    row.trainer_id ??
    null;
  return id != null ? String(id) : "";
};

const filterRowsForEmployee = (rows, employeeId) => {
  if (!employeeId) return rows;
  const target = String(employeeId);
  return (rows || []).filter((row) => {
    if (rowEmployeeId(row) === target) return true;
    if (row.id != null && String(row.id) === target) return true;
    return false;
  });
};

const safeRequestRows = async (requestPromise) => {
  try {
    const response = await requestPromise;
    return normalizeRows(response);
  } catch (error) {
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const safeOptionalRows = async (label, requestPromise) => {
  try {
    const response = await requestPromise;
    return {
      rows: normalizeRows(response),
      failed: false,
      label,
    };
  } catch (error) {
    // Empty datasets commonly return 404 in this API — treat as empty, not failure.
    if (error?.response?.status === 404) {
      return {
        rows: [],
        failed: false,
        label,
      };
    }

    console.error(`HR report optional dataset failed: ${label}`, error);
    return {
      rows: [],
      failed: true,
      label,
    };
  }
};

const buildDutyHoursMap = (staffRows) => {
  const map = {};

  staffRows.forEach((staff) => {
    if (!Array.isArray(staff.duty_hours)) return;

    map[String(staff.id)] = {};
    staff.duty_hours.forEach((slot) => {
      if (String(slot.status) === "1") {
        map[String(staff.id)][slot.day] = {
          start_time: slot.start_time,
          end_time: slot.end_time,
        };
      }
    });
  });

  return map;
};

const SummaryTile = ({ label, value, tone }) => (
  <div className="col-12 col-sm-6 col-xl-2 mb-3">
    <div className={`card radius-10 custom-shadow border-${tone} h-100`}>
      <div className="card-body">
        <p className="text-muted mb-2">{label}</p>
        <h4 className={`mb-0 text-${tone}`}>{value}</h4>
      </div>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="col-md-4 col-sm-6 mb-3">
    <div className="border rounded px-3 py-2 h-100 bg-light">
      <div className="small text-muted">{label}</div>
      <div className="fw-semibold">{value || "N/A"}</div>
    </div>
  </div>
);

const CollapsibleSection = ({
  title,
  tone = "secondary",
  badge,
  defaultOpen = false,
  children,
  className = "",
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`card radius-10 custom-shadow mb-4 hr-report-collapsible ${className}`}>
      <button
        type="button"
        className={`card-header bg-white border-bottom border-${tone} w-100 text-start hr-report-collapsible-toggle`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className={`mb-0 text-${tone} d-flex align-items-center gap-2`}>
            <i
              className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"} hr-report-chevron`}
              aria-hidden
            />
            {title}
          </h5>
          {badge != null ? <span className={`badge bg-${tone}`}>{badge}</span> : null}
        </div>
      </button>
      <div
        className={`card-body hr-report-collapsible-body${
          open ? "" : " hr-report-collapsible-body--closed"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

const ReportTableCard = ({
  title,
  tone,
  rows,
  columns,
  emptyMessage,
  defaultOpen = false,
}) => (
  <CollapsibleSection
    title={title}
    tone={tone}
    badge={`Records: ${rows.length}`}
    defaultOpen={defaultOpen}
  >
    {rows.length > 0 ? (
      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle mb-0">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.label}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || row.record_key || `${title}-${index}`}>
                {columns.map((column) => (
                  <td key={`${column.label}-${row.id || index}`}>
                    {column.render ? column.render(row, index) : row[column.key] || "N/A"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="text-center text-muted py-4">{emptyMessage}</div>
    )}
  </CollapsibleSection>
);

const ScopePill = ({ label, value }) => (
  <div className="d-flex align-items-center gap-2 flex-wrap">
    <span className="small text-muted">{label}</span>
    <span className="badge text-bg-light border">{value || "All"}</span>
  </div>
);

const DetailedHRReport = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessages, setWarningMessages] = useState([]);
  const [reportData, setReportData] = useState(emptyReportState);

  const activeBranch = selectedBranch || branch_id || "";
  const selectedEmployeeProfile = reportData.employees.find(
    (employee) => String(employee.id) === String(selectedEmployee)
  );
  const reportRangeStart = moment(startDate).isAfter(endDate) ? endDate : startDate;
  const reportRangeEnd = moment(startDate).isAfter(endDate) ? startDate : endDate;
  const selectedBranchLabel =
    branches.find((branch) => String(branch.id) === String(activeBranch))?.name || "All Branches";
  const selectedDepartmentLabel =
    departments.find((department) => String(department.id) === String(selectedDepartment))?.name ||
    "All Departments";
  const selectedDesignationLabel =
    designations.find((designation) => String(designation.id) === String(selectedDesignation))
      ?.name || "All Designations";
  const selectedEmployeeLabel =
    employeeOptions.find((employee) => String(employee.id) === String(selectedEmployee))
      ? buildEmployeeName(
          employeeOptions.find((employee) => String(employee.id) === String(selectedEmployee))
        )
      : "All Employees";

  const fetchLookups = async () => {
    setFiltersLoading(true);
    try {
      const [branchResponse, departmentResponse, designationResponse] = await Promise.all([
        UserServices.branchName(),
        UserServices.departmentName(),
        UserServices.designationName(),
      ]);

      setBranches(branchResponse?.data?.data || []);
      setDepartments(departmentResponse?.data?.data || []);
      setDesignations(designationResponse?.data?.data || []);
    } catch (error) {
      console.error("Error loading HR report lookups:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchEmployeeOptions = useCallback(async () => {
    try {
      const rows = await safeRequestRows(
        UserServices.getHrReportEmployees({
          branch_id: activeBranch,
          department_id: selectedDepartment,
          designation_id: selectedDesignation,
          limit: 1000,
        })
      );

      const scopedEmployees = rows.filter(
        (employee) =>
          Number(employee.is_login_account) !== 1 &&
          !PARTNER_DESIGNATION_IDS.includes(parseInt(employee.designation_id, 10))
      );

      setEmployeeOptions(scopedEmployees);

      if (
        selectedEmployee &&
        !scopedEmployees.some((employee) => String(employee.id) === String(selectedEmployee))
      ) {
        setSelectedEmployee("");
      }
    } catch (error) {
      console.error("Error loading employee options:", error);
      setEmployeeOptions([]);
    }
  }, [activeBranch, selectedDepartment, selectedDesignation, selectedEmployee]);

  const calculateLateInfo = (attendance, dutyHoursMap) => {
    const staffId = String(attendance.attendee_id);
    const dayName = moment(attendance.date).format("dddd");
    const dutyHours = dutyHoursMap[staffId]?.[dayName];

    if (!dutyHours?.start_time || !attendance.checkin_time_24h) {
      return {
        isLate: false,
        lateMinutes: 0,
        lateText: "On Time",
        dutyWindow: "N/A",
      };
    }

    const checkIn = moment(attendance.checkin_time_24h, "HH:mm:ss");
    const dutyStart = moment(dutyHours.start_time, "HH:mm");
    const dutyEnd = dutyHours.end_time ? moment(dutyHours.end_time, "HH:mm") : null;

    if (!checkIn.isValid() || !dutyStart.isValid()) {
      return {
        isLate: false,
        lateMinutes: 0,
        lateText: "On Time",
        dutyWindow: "N/A",
      };
    }

    const lateMinutes = checkIn.isAfter(dutyStart)
      ? checkIn.diff(dutyStart, "minutes")
      : 0;

    const lateText =
      lateMinutes > 0
        ? `${Math.floor(lateMinutes / 60) > 0 ? `${Math.floor(lateMinutes / 60)}h ` : ""}${
            lateMinutes % 60
          }m`
        : "On Time";

    return {
      isLate: lateMinutes > 0,
      lateMinutes,
      lateText,
      dutyWindow: `${formatDateTime(dutyHours.start_time, "HH:mm")} - ${
        dutyEnd?.isValid() ? dutyEnd.format("hh:mm A") : dutyHours.end_time || "N/A"
      }`,
    };
  };

  const generateReport = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    setWarningMessages([]);

    try {
      let reportStartDate = startDate;
      let reportEndDate = endDate;

      if (moment(reportStartDate).isAfter(reportEndDate)) {
        [reportStartDate, reportEndDate] = [reportEndDate, reportStartDate];
      }

      const employeeRows = await safeRequestRows(
        UserServices.getHrReportEmployees({
          branch_id: activeBranch,
          department_id: selectedDepartment,
          designation_id: selectedDesignation,
          limit: 1000,
        })
      );

      const scopedEmployees = employeeRows.filter(
        (employee) =>
          Number(employee.is_login_account) !== 1 &&
          !PARTNER_DESIGNATION_IDS.includes(parseInt(employee.designation_id, 10)) &&
          (!selectedEmployee || String(employee.id) === String(selectedEmployee))
      );

      const scopedEmployeeIds = new Set(scopedEmployees.map((employee) => String(employee.id)));
      const employeeMap = {};

      scopedEmployees.forEach((employee) => {
        employeeMap[String(employee.id)] = employee;
      });

      const payrollFilters = {
        startdate: reportStartDate,
        enddate: reportEndDate,
        trainer_id: selectedEmployee || "",
      };

      const attendanceFilters = {
        sdate: reportStartDate,
        edate: reportEndDate,
        start_time: startTime || null,
        end_time: endTime || null,
        gender: null,
      };

      const [
        dutyHourResult,
        attendanceResult,
        leaveResult,
        salaryResult,
        commissionResult,
        fineResult,
        disciplinaryResult,
        advanceResult,
        loanResult,
        promotionResult,
        profileEntryResult,
      ] = await Promise.all([
        safeOptionalRows("Duty Hours", UserServices.getActiveEmployeHours(activeBranch, 1000, 1)),
        safeOptionalRows(
          "Attendance",
          UserServices.getEmployeeAttendence(
            activeBranch,
            attendanceFilters,
            selectedEmployee || null,
            1000,
            1
          )
        ),
        safeOptionalRows(
          "Leaves",
          UserServices.getLeaveApplications(
            activeBranch && Number(activeBranch) !== 0 ? activeBranch : "",
            1000,
            1
          )
        ),
        safeOptionalRows(
          "Payroll",
          UserServices.getHrSalaryRecords({
            branch_id: activeBranch,
            start_date: reportStartDate,
            end_date: reportEndDate,
            user_id: selectedEmployee || "",
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Commission",
          UserServices.getLatestStaffCommision(activeBranch, payrollFilters, 1000, 1)
        ),
        safeOptionalRows(
          "Fines",
          UserServices.getHrFinanceRecords({
            branch_id: activeBranch,
            user_id: selectedEmployee || "",
            category: "Fine",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Disciplinary Actions",
          UserServices.getCertificatesLetters(
            activeBranch,
            1000,
            1,
            selectedEmployee || "",
            {
              document_type: "Warning Letter",
              start_date: reportStartDate,
              end_date: reportEndDate,
              status: "1",
            }
          )
        ),
        safeOptionalRows(
          "Advances",
          UserServices.getHrFinanceRecords({
            branch_id: activeBranch,
            user_id: selectedEmployee || "",
            category: "Advance",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows(
          "Loans",
          UserServices.getHrLoanRecords({
            branch_id: activeBranch,
            staff_id: selectedEmployee || "",
            start_date: reportStartDate,
            end_date: reportEndDate,
            limit: 1000,
          })
        ),
        safeOptionalRows("Promotions", UserServices.getStaffPromotions(activeBranch, 1000, 1)),
        safeOptionalRows(
          "Employee Profile Entries",
          UserServices.getEmployeeProfileEntries({
            branch_id: activeBranch && Number(activeBranch) !== 0 ? activeBranch : "",
            user_id: selectedEmployee || "",
            status: "1",
            limit: 1000,
          })
        ),
      ]);

      const warningLabels = [
        dutyHourResult,
        attendanceResult,
        leaveResult,
        salaryResult,
        commissionResult,
        fineResult,
        disciplinaryResult,
        advanceResult,
        loanResult,
        promotionResult,
        profileEntryResult,
      ]
        .filter((result) => result.failed)
        .map((result) => result.label);

      setWarningMessages(warningLabels);

      const dutyHourRows = dutyHourResult.rows;
      const attendanceRowsRaw = attendanceResult.rows;
      const leaveRowsRaw = leaveResult.rows;
      const salaryRowsRaw = salaryResult.rows;
      const commissionRowsRaw = commissionResult.rows;
      const fineRowsRaw = fineResult.rows;
      const disciplinaryRowsRaw = disciplinaryResult.rows;
      const advanceRowsRaw = advanceResult.rows;
      const loanRowsRaw = loanResult.rows;
      const promotionRowsRaw = promotionResult.rows;
      const profileEntryRowsRaw = profileEntryResult.rows;

      const dutyHoursMap = buildDutyHoursMap(dutyHourRows);

      const attendanceRows = attendanceRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.attendee_id)))
        .map((record) => {
          const employee = employeeMap[String(record.attendee_id)] || {};
          const lateInfo = calculateLateInfo(record, dutyHoursMap);

          return {
            id: record.id,
            employee_name:
              buildEmployeeName(employee, record.user_name || "N/A"),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            date: record.date,
            attendance_status: record.attendance_status || "N/A",
            checkin_time: formatDateTime(record.checkin_time_24h, "HH:mm:ss"),
            checkout_time: formatDateTime(record.checkout_time_24h, "HH:mm:ss"),
            working_hours: record.working_hours || "N/A",
            duty_window: lateInfo.dutyWindow,
            is_late: lateInfo.isLate,
            late_text: lateInfo.lateText,
          };
        })
        .sort((a, b) => moment(b.date).diff(moment(a.date)));

      const leaveRows = leaveRowsRaw
        .filter((record) => {
          const employeeId = String(record.user_id || record.attendee_id || "");
          return scopedEmployeeIds.has(employeeId);
        })
        .map((record, index) => {
          const employeeId = String(record.user_id || record.attendee_id || "");
          const employee = employeeMap[employeeId] || {};
          const leaveFrom = record.from || record.leave_from_date;
          const leaveTo = record.to || record.leave_to_date;

          return {
            id: record.id || `leave-${index}`,
            employee_name:
              record.user_info?.name || buildEmployeeName(employee),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            leave_type: record.leave_type || "N/A",
            leave_from_date: leaveFrom,
            leave_to_date: leaveTo,
            application_status: record.application_status || "N/A",
            leave_status: record.leave_status || "N/A",
            reason: record.reason || record.remarks || "N/A",
          };
        })
        .sort((a, b) => moment(b.leave_from_date).diff(moment(a.leave_from_date)));

      const salaryRows = salaryRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.id)))
        .map((record) => {
          const salary = toNumber(record.salary);
          const commission = toNumber(record.commission?.commission);
          const reward = toNumber(record.reward);
          const advance = toNumber(record.advance);
          const fine = toNumber(record.fine);
          const loan = toNumber(record.loan);
          const cafe = toNumber(record.cafe);
          const detections = toNumber(record.detections);
          const medical = toNumber(record.medical);

          return {
            id: record.id,
            employee_name: record.name,
            department: record.department || "N/A",
            designation: record.designation || "N/A",
            salary,
            reward,
            commission,
            advance,
            fine,
            loan,
            cafe,
            detections,
            medical,
            net_payable:
              salary + commission + reward - advance - fine - loan - cafe - detections - medical,
          };
        })
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const commissionRows = commissionRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.id)))
        .map((record) => ({
          id: record.id,
          employee_name: record.name,
          department: record.department || "N/A",
          designation: record.designation || "N/A",
          commission_per: toNumber(record.commission?.commission_per),
          gross_commission: toNumber(
            record.commission?.gross_commission ?? record.commission?.commission
          ),
          paid_commission: toNumber(record.commission?.paid_commission),
          outstanding_commission: toNumber(record.commission?.outstanding_commission),
          pt_commission: toNumber(record.commission?.pt_commission),
          gx_commission: toNumber(record.commission?.gx_commission),
          small_pt_commission: toNumber(record.commission?.small_pt_commission),
          payout_status: record.commission?.payout_status || "unpaid",
          payout_date: record.commission?.payout_date || null,
        }))
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const fineRows = fineRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.user_id)))
        .map((record) => {
          const employee = employeeMap[String(record.user_id)] || {};
          return {
            id: record.id,
            employee_name: buildEmployeeName(
              employee,
              `${record.user_fname || ""} ${record.user_lname || ""}`.trim() || "N/A"
            ),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            reason: record.reason || "N/A",
            occurrence_date: record.occurrence_date,
            return_month: record.return_month,
          };
        })
        .sort((a, b) => moment(b.occurrence_date).diff(moment(a.occurrence_date)));

      const disciplinaryRows = disciplinaryRowsRaw
        .filter((record) => {
          if (!isDisciplinaryDocument(record)) return false;
          return scopedEmployeeIds.has(String(record.user_id));
        })
        .map((record) => {
          const employee = employeeMap[String(record.user_id)] || {};
          return {
            id: record.id,
            employee_name:
              record.employee?.name ||
              buildEmployeeName(employee, "N/A"),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            document_type: record.document_type || "Warning Letter",
            subject: record.subject || "N/A",
            document_code: record.document_code || "N/A",
            issue_date: record.issue_date,
            approval_status: record.approval_status || "N/A",
            status: String(record.status) === "0" ? "Inactive" : "Active",
            print_path:
              record.document_type === "Warning Letter"
                ? `/warning-letter/${record.id}`
                : null,
          };
        })
        .sort((a, b) => moment(b.issue_date).diff(moment(a.issue_date)));

      const advanceRows = advanceRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.user_id)))
        .map((record) => {
          const employee = employeeMap[String(record.user_id)] || {};
          return {
            id: record.id,
            employee_name: buildEmployeeName(
              employee,
              `${record.user_fname || ""} ${record.user_lname || ""}`.trim() || "N/A"
            ),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            transaction_type: record.transaction_type || "N/A",
            payment_method: record.payment_method || "N/A",
            reason: record.reason || "N/A",
            occurrence_date: record.occurrence_date,
            return_month: record.return_month,
          };
        })
        .sort((a, b) => moment(b.occurrence_date).diff(moment(a.occurrence_date)));

      const loanRows = loanRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.staff_id)))
        .map((record) => {
          const employee = employeeMap[String(record.staff_id)] || {};
          const paidAmount = Array.isArray(record.installmentsDetail)
            ? record.installmentsDetail.reduce(
                (sum, installment) => sum + toNumber(installment.amount),
                0
              )
            : 0;

          return {
            id: record.id,
            employee_name: buildEmployeeName(employee, record.staff_name || "N/A"),
            department: employee.department || "N/A",
            designation: employee.designation || "N/A",
            amount: toNumber(record.amount),
            installment: toNumber(record.installment),
            paid_amount: paidAmount,
            pending_amount: Math.max(0, toNumber(record.amount) - paidAmount),
            reason: record.reason || "N/A",
            date: record.date,
            return_start_date: record.return_start_date,
          };
        })
        .sort((a, b) => moment(b.date).diff(moment(a.date)));

      const promotionRows = promotionRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.employee_id)))
        .map((record) => ({
          id: record.id,
          employee_name: record.employee || "N/A",
          previous_department: record.pervs_depart || "N/A",
          new_department: record.new_depart || "N/A",
          previous_designation: record.pervs_designation || "N/A",
          new_designation: record.new_designation || "N/A",
          previous_salary: toNumber(record.previous_salary),
          new_salary: toNumber(record.new_salary),
          status: String(record.status) === "1" ? "Active" : "Inactive",
        }))
        .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      const profileEntryRows = profileEntryRowsRaw
        .filter((record) => scopedEmployeeIds.has(String(record.user_id)))
        .map((record, index) => {
          const employee = employeeMap[String(record.user_id)] || {};

          return {
            id: record.id || `profile-entry-${index}`,
            employee_name: buildEmployeeName(
              record.employee || employee,
              record.user_info?.name || "N/A"
            ),
            entry_type: String(record.entry_type || "").toLowerCase(),
            title: record.title || "N/A",
            organization: record.organization || "N/A",
            location: record.location || "N/A",
            start_date: record.start_date,
            end_date: record.end_date,
            description: record.description || "N/A",
          };
        })
        .sort((a, b) => {
          const firstDate = a.end_date || a.start_date;
          const secondDate = b.end_date || b.start_date;
          return moment(secondDate).valueOf() - moment(firstDate).valueOf();
        });

      setReportData({
        employees: scopedEmployees,
        attendanceRows,
        leaveRows,
        salaryRows,
        commissionRows,
        fineRows,
        disciplinaryRows,
        advanceRows,
        loanRows,
        promotionRows,
        profileEntryRows,
        summary: {
          employees: scopedEmployees.length,
          present: attendanceRows.filter((record) => record.attendance_status === "Present").length,
          late: attendanceRows.filter((record) => record.is_late).length,
          absent: attendanceRows.filter((record) => record.attendance_status === "Absent").length,
          onLeave: leaveRows.filter((record) => record.application_status === "Approved").length,
          netSalary: salaryRows.reduce((sum, row) => sum + row.net_payable, 0),
          grossCommission: commissionRows.reduce(
            (sum, row) => sum + row.gross_commission,
            0
          ),
          fines: fineRows.reduce((sum, row) => sum + row.amount, 0),
          disciplinary: disciplinaryRows.length,
          advances: advanceRows.reduce((sum, row) => sum + row.amount, 0),
          loanOutstanding: loanRows.reduce((sum, row) => sum + row.pending_amount, 0),
        },
      });
    } catch (error) {
      console.error("Error generating HR report:", error);
      setErrorMessage(
        "Unable to generate the HR report right now. Please review the filters and try again."
      );
      setReportData(emptyReportState);
    } finally {
      setLoading(false);
    }
  }, [
    activeBranch,
    endDate,
    selectedDepartment,
    selectedDesignation,
    selectedEmployee,
    startDate,
    startTime,
    endTime,
  ]);

  const handleReset = () => {
    setSelectedBranch(branch_id || "");
    setSelectedDepartment("");
    setSelectedDesignation("");
    setSelectedEmployee("");
    setStartDate(moment().startOf("month").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
    setStartTime("");
    setEndTime("");
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value).replace(/"/g, "\"\"");
    return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
  };

  const downloadCsv = (rows) => {
    if (!rows.length) return;

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((header) => escapeCsvValue(row[header])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hr-report-${moment().format("YYYYMMDD-HHmmss")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const exportRows = [];

    reportData.employees.forEach((employee) => {
      exportRows.push({
        section: "Employee Profile",
        employee_name: `${employee.first_name || ""} ${employee.last_name || ""}`.trim(),
        date: "",
        amount: "",
        status: employee.employment_status || "N/A",
        detail_1: employee.department || "N/A",
        detail_2: employee.designation || "N/A",
        detail_3: employee.phone || "N/A",
      });
    });

    reportData.attendanceRows.forEach((row) => {
      exportRows.push({
        section: "Attendance",
        employee_name: row.employee_name,
        date: row.date,
        amount: "",
        status: row.attendance_status,
        detail_1: row.duty_window,
        detail_2: row.checkin_time,
        detail_3: row.is_late ? row.late_text : "On Time",
      });
    });

    reportData.leaveRows.forEach((row) => {
      exportRows.push({
        section: "Leave",
        employee_name: row.employee_name,
        date: row.leave_from_date,
        amount: "",
        status: row.application_status,
        detail_1: row.leave_type,
        detail_2: row.leave_to_date,
        detail_3: row.reason,
      });
    });

    reportData.salaryRows.forEach((row) => {
      exportRows.push({
        section: "Salary",
        employee_name: row.employee_name,
        date: "",
        amount: row.net_payable,
        status: "Net Payable",
        detail_1: row.salary,
        detail_2: row.commission,
        detail_3: row.detections,
      });
    });

    reportData.commissionRows.forEach((row) => {
      exportRows.push({
        section: "Commission",
        employee_name: row.employee_name,
        date: row.payout_date || "",
        amount: row.gross_commission,
        status: row.payout_status,
        detail_1: row.paid_commission,
        detail_2: row.outstanding_commission,
        detail_3: row.commission_per,
      });
    });

    reportData.fineRows.forEach((row) => {
      exportRows.push({
        section: "Fine",
        employee_name: row.employee_name,
        date: row.occurrence_date,
        amount: row.amount,
        status: "Fine",
        detail_1: row.return_month,
        detail_2: row.reason,
        detail_3: "",
      });
    });

    (reportData.disciplinaryRows || []).forEach((row) => {
      exportRows.push({
        section: "Disciplinary Action",
        employee_name: row.employee_name,
        date: row.issue_date,
        amount: "",
        status: row.approval_status || row.status,
        detail_1: row.document_type,
        detail_2: row.subject,
        detail_3: row.document_code,
      });
    });

    reportData.advanceRows.forEach((row) => {
      exportRows.push({
        section: "Advance",
        employee_name: row.employee_name,
        date: row.occurrence_date,
        amount: row.amount,
        status: row.transaction_type,
        detail_1: row.payment_method,
        detail_2: row.return_month,
        detail_3: row.reason,
      });
    });

    reportData.loanRows.forEach((row) => {
      exportRows.push({
        section: "Loan",
        employee_name: row.employee_name,
        date: row.date,
        amount: row.amount,
        status: "Loan",
        detail_1: row.installment,
        detail_2: row.paid_amount,
        detail_3: row.pending_amount,
      });
    });

    reportData.promotionRows.forEach((row) => {
      exportRows.push({
        section: "Promotion",
        employee_name: row.employee_name,
        date: "",
        amount: row.new_salary,
        status: row.status,
        detail_1: row.previous_designation,
        detail_2: row.new_designation,
        detail_3: row.new_department,
      });
    });

    downloadCsv(exportRows);
  };

  const handlePrint = () => {
    window.print();
  };

  const singleEmployeeMode = Boolean(selectedEmployee);
  const attendanceRowsView = filterRowsForEmployee(
    reportData.attendanceRows,
    selectedEmployee
  );
  const leaveRowsView = filterRowsForEmployee(reportData.leaveRows, selectedEmployee);
  const salaryRowsView = filterRowsForEmployee(reportData.salaryRows, selectedEmployee);
  const commissionRowsView = filterRowsForEmployee(reportData.commissionRows, selectedEmployee);
  const fineRowsView = filterRowsForEmployee(reportData.fineRows, selectedEmployee);
  const disciplinaryRowsView = filterRowsForEmployee(
    reportData.disciplinaryRows || [],
    selectedEmployee
  );
  const advanceRowsView = filterRowsForEmployee(reportData.advanceRows, selectedEmployee);
  const loanRowsView = filterRowsForEmployee(reportData.loanRows, selectedEmployee);
  const promotionRowsView = filterRowsForEmployee(reportData.promotionRows, selectedEmployee);

  const selectedEmployeeAttendance = attendanceRowsView;
  const selectedEmployeeLeaves = leaveRowsView;
  const selectedEmployeePayroll = salaryRowsView[0] || null;
  const selectedEmployeeCommission = commissionRowsView[0] || null;
  const selectedEmployeeFineTotal = fineRowsView.reduce((sum, row) => sum + row.amount, 0);
  const selectedEmployeeDisciplinaryCount = disciplinaryRowsView.length;
  const selectedEmployeeAdvanceTotal = advanceRowsView.reduce((sum, row) => sum + row.amount, 0);
  const selectedEmployeeLoanOutstanding = loanRowsView.reduce(
    (sum, row) => sum + row.pending_amount,
    0
  );

  const profileEntryRowsView = filterRowsForEmployee(
    reportData.profileEntryRows || [],
    selectedEmployee
  );
  const qualificationRows = profileEntryRowsView.filter(
    (row) => row.entry_type === "qualification"
  );
  const experienceRows = profileEntryRowsView.filter((row) => row.entry_type === "experience");
  const educationRows = profileEntryRowsView.filter((row) => row.entry_type === "education");
  const selectedEmployeeQualifications = qualificationRows;
  const selectedEmployeeExperience = experienceRows;
  const selectedEmployeeEducation = educationRows;

  useEffect(() => {
    if (!branch_id) return;
    setSelectedBranch(branch_id);
  }, [branch_id]);

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchEmployeeOptions();
  }, [fetchEmployeeOptions]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const timer = setTimeout(() => {
      generateReport();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    activeBranch,
    endDate,
    generateReport,
    selectedDepartment,
    selectedDesignation,
    selectedEmployee,
    startDate,
    startTime,
    endTime,
  ]);

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <style>
            {`
              @media print {
                .hr-report-no-print {
                  display: none !important;
                }

                .page-content,
                .site-main-design,
                .card {
                  padding: 0 !important;
                  margin: 0 !important;
                  box-shadow: none !important;
                }

                .hr-report-print-break {
                  page-break-before: always;
                }

                .hr-report-collapsible-body--closed {
                  display: block !important;
                }
              }

              .hr-report-collapsible-body--closed {
                display: none;
              }

              .hr-report-collapsible-toggle {
                cursor: pointer;
                border: none;
                padding: 1rem 1.25rem;
              }

              .hr-report-collapsible-toggle:hover {
                background-color: #f8fafc !important;
              }

              .hr-report-chevron {
                font-size: 0.95rem;
                opacity: 0.75;
              }
            `}
          </style>
          <div className="hr-report-no-print">
            <MenuButton />
          </div>

          <div className="card radius-10 custom-shadow mb-4 border-0 overflow-hidden">
            <div
              className="card-body p-4"
              style={{ background: "linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)" }}
            >
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <h3 className="mb-1">Detailed HR Report</h3>
                  <p className="text-muted mb-0">
                    Employee-wise and combined HR reporting for attendance, leave, payroll, fines,
                    advances, commissions, loans, and movement history.
                  </p>
                </div>
                <div className="text-md-end">
                  <div className="small text-muted">Generated</div>
                  <div className="fw-semibold">{moment().format("DD MMM YYYY, hh:mm A")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card radius-10 custom-shadow mb-4 hr-report-no-print">
            <div className="card-header bg-white">
              <h5 className="mb-0">Report Filters</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-light border mb-3">
                The report refreshes automatically when you change branch, employee, department,
                designation, date range, or time range.
              </div>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Department</label>
                  <select
                    className="form-select"
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Designation</label>
                  <select
                    className="form-select"
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                  >
                    <option value="">All Designations</option>
                    {designations.map((designation) => (
                      <option key={designation.id} value={designation.id}>
                        {designation.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={filtersLoading}
                  >
                    <option value="">All Employees</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {`${employee.first_name || ""} ${employee.last_name || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <button className="btn btn-primary" onClick={generateReport} disabled={loading}>
                  {loading ? "Generating..." : "Generate Report"}
                </button>
                <button className="btn btn-outline-secondary" onClick={handleReset} disabled={loading}>
                  Reset Filters
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleExportCsv}
                  disabled={loading || reportData.employees.length === 0}
                >
                  Export CSV
                </button>
                <button
                  className="btn btn-outline-dark"
                  onClick={handlePrint}
                  disabled={loading || reportData.employees.length === 0}
                >
                  Print
                </button>
              </div>
            </div>
          </div>

          {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
          {warningMessages.length ? (
            <div className="alert alert-warning">
              Some HR sections could not be loaded for this report: {warningMessages.join(", ")}.
              The remaining data is still shown below.
            </div>
          ) : null}

          {loading ? (
            <div className="card radius-10 custom-shadow mb-4">
              <div className="card-body">
                <Skeleton height={180} />
                <Skeleton count={6} height={30} className="mt-3" />
              </div>
            </div>
          ) : null}

          {!loading && selectedEmployeeProfile ? (
            <CollapsibleSection
              title="Employee Profile Snapshot"
              tone="primary"
              badge={`${selectedEmployeeProfile.first_name || ""} ${
                selectedEmployeeProfile.last_name || ""
              }`.trim()}
            >
              <div className="d-flex justify-content-end mb-3 hr-report-no-print">
                <Link
                  className="btn btn-sm btn-outline-primary"
                  to={`/staff-profile/${selectedEmployeeProfile.id}`}
                >
                  Open full staff profile
                </Link>
              </div>
              <div className="row">
                  <DetailItem label="Employee ID" value={selectedEmployeeProfile.uid} />
                  <DetailItem
                    label="Full Name"
                    value={`${selectedEmployeeProfile.first_name || ""} ${
                      selectedEmployeeProfile.last_name || ""
                    }`.trim()}
                  />
                  <DetailItem label="Branch" value={selectedEmployeeProfile.branch_name} />
                  <DetailItem label="Department" value={selectedEmployeeProfile.department} />
                  <DetailItem label="Designation" value={selectedEmployeeProfile.designation} />
                  <DetailItem label="Salary" value={formatCurrency(selectedEmployeeProfile.salary)} />
                  <DetailItem label="Commission %" value={selectedEmployeeProfile.commission || 0} />
                  <DetailItem label="Joining Date" value={formatDate(selectedEmployeeProfile.joining)} />
                  <DetailItem label="Date of Birth" value={formatDate(selectedEmployeeProfile.dob)} />
                  <DetailItem label="Father Name" value={selectedEmployeeProfile.father_name} />
                  <DetailItem label="Phone" value={selectedEmployeeProfile.phone} />
                  <DetailItem label="Email" value={selectedEmployeeProfile.email} />
                  <DetailItem label="Official Email" value={selectedEmployeeProfile.official_email} />
                  <DetailItem label="CNIC" value={selectedEmployeeProfile.cnic} />
                  <DetailItem label="Employment Status" value={selectedEmployeeProfile.employment_status} />
                  <DetailItem
                    label="Appointment Date"
                    value={formatDate(selectedEmployeeProfile.appointment_date)}
                  />
                  <DetailItem label="Probation Duration" value={selectedEmployeeProfile.probation_duration} />
                  <DetailItem
                    label="Confirmation Date"
                    value={formatDate(selectedEmployeeProfile.confirmation_date)}
                  />
                  <DetailItem
                    label="Employment End Date"
                    value={formatDate(selectedEmployeeProfile.employment_end_date)}
                  />
                  <DetailItem
                    label="Emergency Contact"
                    value={selectedEmployeeProfile.emergency_contact_no}
                  />
                  <DetailItem label="Blood Group" value={selectedEmployeeProfile.blood_group} />
                  <DetailItem
                    label="Monthly Medical"
                    value={formatCurrency(selectedEmployeeProfile.monthly_medical)}
                  />
                  <DetailItem label="Address" value={selectedEmployeeProfile.address} />
                  <DetailItem label="City" value={selectedEmployeeProfile.city} />
                  <DetailItem label="Country" value={selectedEmployeeProfile.country} />
                </div>
            </CollapsibleSection>
          ) : null}

          {!loading ? (
            <CollapsibleSection
              title="Report scope"
              tone="secondary"
              badge={`${formatDate(reportRangeStart)} – ${formatDate(reportRangeEnd)}`}
            >
              <p className="text-muted mb-3">
                Prepared from the current HR data sources and filters.
              </p>
              <div className="row g-3">
                <div className="col-md-3">
                  <ScopePill label="Branch" value={selectedBranchLabel} />
                </div>
                <div className="col-md-3">
                  <ScopePill label="Department" value={selectedDepartmentLabel} />
                </div>
                <div className="col-md-3">
                  <ScopePill label="Designation" value={selectedDesignationLabel} />
                </div>
                <div className="col-md-3">
                  <ScopePill label="Employee" value={selectedEmployeeLabel} />
                </div>
                <div className="col-md-3">
                  <ScopePill label="From Time" value={startTime || "All Times"} />
                </div>
                <div className="col-md-3">
                  <ScopePill label="To Time" value={endTime || "All Times"} />
                </div>
              </div>
            </CollapsibleSection>
          ) : null}

          {!loading && !singleEmployeeMode ? (
            <CollapsibleSection
              title="Organisation summary"
              tone="dark"
              badge={`${reportData.summary.employees} employees`}
            >
              <div className="row">
                <SummaryTile label="Employees in Scope" value={reportData.summary.employees} tone="primary" />
                <SummaryTile label="Present Marks" value={reportData.summary.present} tone="success" />
                <SummaryTile label="Late Marks" value={reportData.summary.late} tone="warning" />
                <SummaryTile label="Absent Marks" value={reportData.summary.absent} tone="danger" />
                <SummaryTile label="Approved Leaves" value={reportData.summary.onLeave} tone="info" />
                <SummaryTile label="Net Salary" value={formatCurrency(reportData.summary.netSalary)} tone="dark" />
                <SummaryTile
                  label="Gross Commission"
                  value={formatCurrency(reportData.summary.grossCommission)}
                  tone="secondary"
                />
                <SummaryTile label="Fines" value={formatCurrency(reportData.summary.fines)} tone="danger" />
                <SummaryTile
                  label="Disciplinary Actions"
                  value={reportData.summary.disciplinary || 0}
                  tone="danger"
                />
                <SummaryTile label="Advances" value={formatCurrency(reportData.summary.advances)} tone="warning" />
                <SummaryTile
                  label="Loan Outstanding"
                  value={formatCurrency(reportData.summary.loanOutstanding)}
                  tone="primary"
                />
              </div>
            </CollapsibleSection>
          ) : null}

          {!loading && selectedEmployeeProfile ? (
            <CollapsibleSection title="Employee 360 HR Summary" tone="primary">
              <div className="row">
                  <SummaryTile
                    label="Present Marks"
                    value={
                      selectedEmployeeAttendance.filter(
                        (row) => row.attendance_status === "Present"
                      ).length
                    }
                    tone="success"
                  />
                  <SummaryTile
                    label="Late Marks"
                    value={selectedEmployeeAttendance.filter((row) => row.is_late).length}
                    tone="warning"
                  />
                  <SummaryTile
                    label="Approved Leaves"
                    value={
                      selectedEmployeeLeaves.filter(
                        (row) => row.application_status === "Approved"
                      ).length
                    }
                    tone="info"
                  />
                  <SummaryTile
                    label="Net Salary"
                    value={formatCurrency(selectedEmployeePayroll?.net_payable || 0)}
                    tone="dark"
                  />
                  <SummaryTile
                    label="Gross Commission"
                    value={formatCurrency(selectedEmployeeCommission?.gross_commission || 0)}
                    tone="secondary"
                  />
                  <SummaryTile
                    label="Outstanding Loan"
                    value={formatCurrency(selectedEmployeeLoanOutstanding)}
                    tone="primary"
                  />
                  <SummaryTile
                    label="Fines"
                    value={formatCurrency(selectedEmployeeFineTotal)}
                    tone="danger"
                  />
                  <SummaryTile
                    label="Disciplinary Actions"
                    value={selectedEmployeeDisciplinaryCount}
                    tone="danger"
                  />
                  <SummaryTile
                    label="Advances"
                    value={formatCurrency(selectedEmployeeAdvanceTotal)}
                    tone="warning"
                  />
                </div>
            </CollapsibleSection>
          ) : null}

          {!loading ? (
            <>
              <ReportTableCard
                title="Qualifications / Certifications"
                tone="primary"
                rows={qualificationRows}
                emptyMessage={
                  selectedEmployee
                    ? "No qualifications or certifications found for the selected employee."
                    : "No qualifications or certifications found for the selected scope."
                }
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  ...(selectedEmployee
                    ? []
                    : [{ label: "Employee", key: "employee_name" }]),
                  { label: "Title", key: "title" },
                  { label: "Institute / Company", key: "organization" },
                  { label: "Location", key: "location" },
                  { label: "Start Date", render: (row) => formatDate(row.start_date) },
                  { label: "End Date", render: (row) => formatDate(row.end_date) },
                  { label: "Description", key: "description" },
                ]}
              />

              <ReportTableCard
                title="Experience"
                tone="info"
                rows={experienceRows}
                emptyMessage={
                  selectedEmployee
                    ? "No experience records found for the selected employee."
                    : "No experience records found for the selected scope."
                }
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  ...(selectedEmployee
                    ? []
                    : [{ label: "Employee", key: "employee_name" }]),
                  { label: "Title", key: "title" },
                  { label: "Company", key: "organization" },
                  { label: "Location", key: "location" },
                  { label: "Start Date", render: (row) => formatDate(row.start_date) },
                  { label: "End Date", render: (row) => formatDate(row.end_date) },
                  { label: "Description", key: "description" },
                ]}
              />

              <ReportTableCard
                title="Education"
                tone="success"
                rows={educationRows}
                emptyMessage={
                  selectedEmployee
                    ? "No education records found for the selected employee."
                    : "No education records found for the selected scope."
                }
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  ...(selectedEmployee
                    ? []
                    : [{ label: "Employee", key: "employee_name" }]),
                  { label: "Title", key: "title" },
                  { label: "Institute", key: "organization" },
                  { label: "Location", key: "location" },
                  { label: "Start Date", render: (row) => formatDate(row.start_date) },
                  { label: "End Date", render: (row) => formatDate(row.end_date) },
                  { label: "Description", key: "description" },
                ]}
              />

              {!singleEmployeeMode ? (
                <ReportTableCard
                  title="Employee Scope Register"
                  tone="primary"
                  rows={reportData.employees}
                  emptyMessage="No employees matched the selected report filters."
                  columns={[
                    { label: "S.No", render: (_, index) => index + 1 },
                    {
                      label: "Employee",
                      render: (row) =>
                        `${row.first_name || ""} ${row.last_name || ""}`.trim() || "N/A",
                    },
                    { label: "Employee ID", key: "uid" },
                    { label: "Branch", key: "branch_name" },
                    { label: "Department", key: "department" },
                    { label: "Designation", key: "designation" },
                    { label: "Salary", render: (row) => formatCurrency(row.salary) },
                    { label: "Commission %", key: "commission" },
                    { label: "Joining", render: (row) => formatDate(row.joining) },
                    { label: "Phone", key: "phone" },
                  ]}
                />
              ) : null}

              <ReportTableCard
                title="Attendance Register"
                tone="success"
                rows={attendanceRowsView}
                emptyMessage="No attendance records found for the selected criteria."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Date", render: (row) => formatDate(row.date) },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Duty Hours", key: "duty_window" },
                  { label: "Check In", key: "checkin_time" },
                  { label: "Check Out", key: "checkout_time" },
                  { label: "Working Hours", key: "working_hours" },
                  {
                    label: "Status",
                    render: (row) => (
                      <span
                        className={`badge bg-${
                          row.is_late ? "warning" : row.attendance_status === "Absent" ? "danger" : "success"
                        }`}
                      >
                        {row.is_late ? "Late" : row.attendance_status}
                      </span>
                    ),
                  },
                  { label: "Late By", key: "late_text" },
                ]}
              />

              <ReportTableCard
                title="Leave Register"
                tone="info"
                rows={leaveRowsView}
                emptyMessage="No leave records found for the selected staff scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Type", key: "leave_type" },
                  { label: "From", render: (row) => formatDate(row.leave_from_date) },
                  { label: "To", render: (row) => formatDate(row.leave_to_date) },
                  { label: "Leave Status", key: "leave_status" },
                  {
                    label: "Approval",
                    render: (row) => (
                      <span
                        className={`badge bg-${
                          row.application_status === "Approved"
                            ? "success"
                            : row.application_status === "Rejected"
                            ? "danger"
                            : "warning"
                        }`}
                      >
                        {row.application_status}
                      </span>
                    ),
                  },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Payroll Register"
                tone="dark"
                rows={salaryRowsView}
                emptyMessage="No payroll records were returned for this report scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Base Salary", render: (row) => formatCurrency(row.salary) },
                  { label: "Commission", render: (row) => formatCurrency(row.commission) },
                  { label: "Reward", render: (row) => formatCurrency(row.reward) },
                  { label: "Advance", render: (row) => formatCurrency(row.advance) },
                  { label: "Fine", render: (row) => formatCurrency(row.fine) },
                  { label: "Loan", render: (row) => formatCurrency(row.loan) },
                  { label: "Deduction", render: (row) => formatCurrency(row.detections) },
                  { label: "Net Payable", render: (row) => formatCurrency(row.net_payable) },
                ]}
              />

              <ReportTableCard
                title="Commission Register"
                tone="secondary"
                rows={commissionRowsView}
                emptyMessage="No commission records were returned for this report scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Commission %", key: "commission_per" },
                  { label: "Gross", render: (row) => formatCurrency(row.gross_commission) },
                  { label: "Paid", render: (row) => formatCurrency(row.paid_commission) },
                  { label: "Outstanding", render: (row) => formatCurrency(row.outstanding_commission) },
                  { label: "PT", render: (row) => formatCurrency(row.pt_commission) },
                  { label: "GX", render: (row) => formatCurrency(row.gx_commission) },
                  { label: "SPT", render: (row) => formatCurrency(row.small_pt_commission) },
                  {
                    label: "Payout",
                    render: (row) => (
                      <span className={`badge bg-${row.payout_status === "paid" ? "success" : "warning"}`}>
                        {row.payout_status}
                      </span>
                    ),
                  },
                ]}
              />

              <ReportTableCard
                title="Fine Register"
                tone="danger"
                rows={fineRowsView}
                emptyMessage="No fines were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Occurrence Date", render: (row) => formatDate(row.occurrence_date) },
                  { label: "Return Month", render: (row) => formatDate(row.return_month) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Disciplinary Action Register"
                tone="danger"
                rows={disciplinaryRowsView}
                emptyMessage="No disciplinary actions / warning letters were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Type", key: "document_type" },
                  { label: "Subject", key: "subject" },
                  { label: "Issue Date", render: (row) => formatDate(row.issue_date) },
                  { label: "Code", key: "document_code" },
                  { label: "Approval", key: "approval_status" },
                  { label: "Status", key: "status" },
                  {
                    label: "Action",
                    render: (row) =>
                      row.print_path ? (
                        <Link className="btn btn-sm btn-outline-primary" to={row.print_path}>
                          Print
                        </Link>
                      ) : (
                        "N/A"
                      ),
                  },
                ]}
              />

              <ReportTableCard
                title="Advance Register"
                tone="warning"
                rows={advanceRowsView}
                emptyMessage="No advances were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Type", key: "transaction_type" },
                  { label: "Payment Method", key: "payment_method" },
                  { label: "Occurrence Date", render: (row) => formatDate(row.occurrence_date) },
                  { label: "Recovery Month", render: (row) => formatDate(row.return_month) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Loan Register"
                tone="primary"
                rows={loanRowsView}
                emptyMessage="No loans were found for the selected scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Department", key: "department" },
                  { label: "Designation", key: "designation" },
                  { label: "Loan Amount", render: (row) => formatCurrency(row.amount) },
                  { label: "Installment", render: (row) => formatCurrency(row.installment) },
                  { label: "Paid", render: (row) => formatCurrency(row.paid_amount) },
                  { label: "Outstanding", render: (row) => formatCurrency(row.pending_amount) },
                  { label: "Issue Date", render: (row) => formatDate(row.date) },
                  { label: "Recovery Start", render: (row) => formatDate(row.return_start_date) },
                  { label: "Reason", key: "reason" },
                ]}
              />

              <ReportTableCard
                title="Promotion History"
                tone="dark"
                rows={promotionRowsView}
                emptyMessage="No promotion history was found in the selected HR scope."
                columns={[
                  { label: "S.No", render: (_, index) => index + 1 },
                  { label: "Employee", key: "employee_name" },
                  { label: "Previous Department", key: "previous_department" },
                  { label: "New Department", key: "new_department" },
                  { label: "Previous Designation", key: "previous_designation" },
                  { label: "New Designation", key: "new_designation" },
                  { label: "Previous Salary", render: (row) => formatCurrency(row.previous_salary) },
                  { label: "New Salary", render: (row) => formatCurrency(row.new_salary) },
                  { label: "Status", key: "status" },
                ]}
              />
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default DetailedHRReport;
